import { ConnectionItem, Flex, Text } from "@wayflyer/flyui";
import { IconBank24Line } from "@wayflyer/flyui-icons/24/line";
import type {
  BankAccountGroupSchema,
  BankConnectionSchema,
  LogoLoadingState,
  UngroupedStatementSchema,
} from "@wayflyer/customer-bankconnect";
import dayjs from "dayjs";
import { useRef, type ReactElement, type ReactNode } from "react";
import { useIntl } from "react-intl";

import { BankLogoDisplay } from "../../../../components";
import { MESSAGES } from "../message-definitions";
import { StaggerItem } from "../stagger-item/stagger-item";

import { useSlideOnMove } from "./use-slide-on-move";
import type { SelectedAccount } from "../utils";
import {
  getAccountLabel,
  getAccountReadinessBadge,
  getConnectedSubtitle,
  getGroupAccountLabel,
  getGroupSubtitle,
  hasBankAccounts,
} from "../utils";

type BankAccountsListProps = {
  accountGroups: BankAccountGroupSchema[];
  ungroupedConnections: BankConnectionSchema[];
  ungroupedStatements: UngroupedStatementSchema[];
  logoLoadingStates: LogoLoadingState;
  requiredStatementMonths: number;
  footer: ReactNode;
  onSelectAccount: (selectedAccount: SelectedAccount) => void;
  onSelectGroup: (group: BankAccountGroupSchema) => void;
  onSelectUnmatchedStatements: () => void;
};

export const BankAccountsList = ({
  accountGroups,
  ungroupedConnections,
  ungroupedStatements,
  logoLoadingStates,
  requiredStatementMonths,
  footer,
  onSelectAccount,
  onSelectGroup,
  onSelectUnmatchedStatements,
}: BankAccountsListProps) => {
  const intl = useIntl();
  const now = dayjs();
  const footerAnchorRef = useRef<HTMLDivElement>(null);
  const footerSlideRef = useRef<HTMLDivElement>(null);

  const groupItems: { sortLabel: string; node: ReactElement }[] = accountGroups.map((group) => {
    const connectionMember = group.connectionMembers[0];
    const connection = connectionMember?.connection;
    const { badgeStatus, badgeText } = getAccountReadinessBadge(intl, group, requiredStatementMonths, now);
    const label = getGroupAccountLabel(intl, group);

    return {
      sortLabel: label,
      node: (
        <ConnectionItem
          key={`group-${group.uuid}`}
          label={label}
          logo={
            connection ? (
              <BankLogoDisplay logoState={logoLoadingStates[connection.institutionID]} />
            ) : (
              <IconBank24Line />
            )
          }
          subtitle={getGroupSubtitle(intl, group)}
          badgeStatus={badgeStatus}
          badgeText={badgeText}
          actions={[
            {
              content: intl.formatMessage(MESSAGES.manageAccountAction),
              onAction: () => onSelectGroup(group),
            },
          ]}
        />
      ),
    };
  });

  const ungroupedConnectionItems: { sortLabel: string; node: ReactElement }[] = ungroupedConnections.flatMap(
    (connection) =>
      connection.accounts
        .filter((account) => account.pull_enabled)
        .map((account) => {
          const label = getAccountLabel(intl, connection, account);

          return {
            sortLabel: label,
            node: (
              <ConnectionItem
                key={account.id}
                label={label}
                logo={<BankLogoDisplay logoState={logoLoadingStates[connection.institutionID]} />}
                subtitle={getConnectedSubtitle(intl, connection, account)}
                badgeStatus="positive"
                badgeText={intl.formatMessage(MESSAGES.readyBadge)}
                variant="positive"
                actions={[
                  {
                    content: intl.formatMessage(MESSAGES.manageAccountAction),
                    onAction: () => onSelectAccount({ connection, account }),
                  },
                ]}
              />
            ),
          };
        })
  );

  const sortedAccountItems = [...groupItems, ...ungroupedConnectionItems].sort((a, b) =>
    a.sortLabel.localeCompare(b.sortLabel)
  );

  const bankAccountCount = sortedAccountItems.length;

  const unmatchedStatementsItem =
    ungroupedStatements.length > 0 ? (
      <ConnectionItem
        key="unmatched-statements"
        label={
          hasBankAccounts(accountGroups, ungroupedConnections)
            ? intl.formatMessage(MESSAGES.otherStatementsUploadedLabel)
            : intl.formatMessage(MESSAGES.statementsUploadedCountLabel, { count: ungroupedStatements.length })
        }
        subtitle={intl.formatMessage(MESSAGES.otherStatementsUploadedSubtitle)}
        logo={<IconBank24Line />}
        badgeStatus="positive"
        badgeText={intl.formatMessage(MESSAGES.readyBadge)}
        actions={[
          {
            content: intl.formatMessage(MESSAGES.viewAction),
            onAction: onSelectUnmatchedStatements,
          },
        ]}
      />
    ) : null;

  const accountItems = [
    ...sortedAccountItems.map((item) => item.node),
    ...(unmatchedStatementsItem ? [unmatchedStatementsItem] : []),
  ];

  useSlideOnMove(footerAnchorRef, footerSlideRef, accountItems.length);

  const hasAccountItems = accountItems.length > 0;
  return (
    <Flex direction="column" gap="6">
      {hasAccountItems && (
        <Flex direction="column" gap="2" maxWidth="594px">
          <StaggerItem index={0} variant="fade">
            <Flex gap="2">
              <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.bankAccountsSectionTitle)}</Text>
              <Text color="subtle">{bankAccountCount}</Text>
            </Flex>
          </StaggerItem>
          <Flex direction="column" gap="2">
            {accountItems.map((item, index) => (
              <StaggerItem key={item.key} index={index + 1}>
                {item}
              </StaggerItem>
            ))}
          </Flex>
        </Flex>
      )}
      <div ref={footerAnchorRef}>
        <div ref={footerSlideRef}>{footer}</div>
      </div>
    </Flex>
  );
};
